export const APPEARANCE = {
  LIGHT: "light",
  DARK: "dark",
} as const;

/**
 * Resolved theme applied to the page — always concrete ("light" or "dark").
 * Distinct from `StoredAppearancePreference` which includes "system" and "".
 */
export type Appearance = (typeof APPEARANCE)[keyof typeof APPEARANCE];

/** Preference value representing "follow the device's prefers-color-scheme". */
export const APPEARANCE_SYSTEM = "system" as const;

/**
 * What the user explicitly chose and we persist in localStorage.
 *
 * Intentionally wider than `Appearance` (the *resolved* theme) because
 * "system" delegates to the OS and "" means "no explicit choice yet".
 */
export type StoredAppearancePreference =
  | Appearance
  | typeof APPEARANCE_SYSTEM
  | "";

// ── Helpers ──────────────────────────────────────────────────────────

/** Type-guard: is the value a resolved appearance ("light" | "dark")? */
export const isAppearance = (value: unknown): value is Appearance =>
  value === APPEARANCE.LIGHT || value === APPEARANCE.DARK;

/** Type-guard: is the value a valid storable preference (light/dark/system)? */
export const isStoredPreference = (
  value: unknown,
): value is Exclude<StoredAppearancePreference, ""> =>
  isAppearance(value) || value === APPEARANCE_SYSTEM;

export const VARIABLE_MAP = {
  sidebar_background_color: "--color-community-sidebar-background",
  header_background_color: "--color-community-header-background",
  header_text_color: "--color-community-header-text",
  header_active_text_color: "--color-community-header-active-text",
  header_active_background_color: "--color-community-header-active-background",
  link_color: "--color-text-link",
  sidebar_text_color: "--color-community-sidebar-text",
  active_item_color: "--color-community-sidebar-active-background",
  active_item_text_color: "--color-community-active-item-text-color",
  sidebar_hover_color: "--color-community-sidebar-hover-background",
  online_indicator_color: "--color-online-background",
  mention_badge_color: "--color-community-sidebar-badge-background",
} as const;

export type VariableOptions = keyof typeof VARIABLE_MAP;
